<template>
  <a v-if="showButton" ref="arButton" rel="ar" :href="modelUrl" type="model/vnd.usdz+zip" class="button filled white product-viewer-button" @click="openViewer">
    <template v-if="loadingModel"><span class="loader tiny"></span></template>
    <template v-else>
      <img class="ar-icon" :src="icon" />
      <span v-if="loadingViewer && iOSChrome">Loading</span>
      <span v-else>View in your space<template v-if="!supportsAR() && !debugButton"> on iOS</template></span>
      <span v-if="loadingViewer && iOSChrome" class="ar-button-loader loader tiny"></span>
    </template>
  </a>
  <ProductViewerQRModal :show="showModal" :url="arLaunchUrl" @update:show="closeQRModal"></ProductViewerQRModal>
</template>
<script>
import { BrowserUtils } from '@/pixel/index';
import SurveyUtils from '@/components/commons/SurveyUtils.js';
import ARIcon from '@/assets/product/icon-ios-ar.svg';
import { ref, onMounted, computed, watch } from 'vue';
import icon from '@/assets/product/icon-ios-ar.png';
import ProductViewerQRModal from '@/components/product/ProductViewerQRModal.vue';
import { supportsAR, supportsQRCode, buildUrl, buildARLaunchUrl, loadARModel, showDebugARButton } from '@/components/commons/AugmentedReality.js';
import useLifecycleHook from '@/compositions/useLifecycleHook.js';
import { useRoute, useRouter } from 'vue-router';
import Tracking from '@/components/commons/Tracking.js';

export default {
  components: {
    ARIcon,
    ProductViewerQRModal,
  },
  props: {
    product: {
      type: Object,
      default: null
    },
  },
  async setup(props, ctx) {
    const { mounted } = useLifecycleHook();
    const router = useRouter();
    const route = useRoute();

    const showModal = ref(false);
    const arButton = ref(null);
    const loadingViewer = ref(false);
    const model = ref(null);
    const loadingModel = ref(true);
    const iOSChrome = ref(false);

    const modelUrl = computed(() => mounted && model.value && model.value.url ? buildUrl(model.value.url, `${import.meta.env.VITE_STOREFRONT_BASE_URL}${route.path}`, props.product.slug) : '');
    const arLaunchUrl = computed(() => mounted && props.product ? buildARLaunchUrl(props.product.hashKey, props.product.slug, router) : '');
    const showButton = computed(() => mounted && (modelUrl.value && !loadingModel.value && (supportsAR() || showDebugARButton(route) || supportsQRCode())));
    const debugButton = computed(() => showDebugARButton(route));

    watch(() => props.product, async () => {
      load();
    });

    async function load() {
      if(props.product && props.product.hashKey) {
        loadingModel.value = true;
        model.value = await loadARModel(props.product.hashKey);
        loadingModel.value = false;
      } else {
        console.warn('Failed to load AR model');
      }
    }

    function startLoadingViewer() {
      loadingViewer.value = true;
      document.addEventListener('visibilitychange', onViewAR);
      setTimeout(() => {
        clearLoadingViewer();
      }, 20000);
    }

    onMounted(async () => {
      // Chrome sometimes delays opening the AR viewer until the model is downloaded.
      // While it is loading, we show a loading state on the button, however we can't do this for
      // Safari as the state change on the button causes Safari to treat the button as a regular
      // download button.
      iOSChrome.value = BrowserUtils.isChromeiOS();

      await load();
    });

    function onViewAR() {
      if (document.visibilityState === 'hidden') {
        clearLoadingViewer();
      }
    }

    function clearLoadingViewer() {
      loadingViewer.value = false;
      document.removeEventListener('visibilitychange', onViewAR);
    }

    async function openViewer(e) {
      if(supportsQRCode() && !debugButton.value) {
        Tracking.track('Click AR QR Code Button');
        showModal.value = true;
        e.preventDefault();
      } else if(!model.value || loadingModel.value) {
        e.preventDefault();
      } else if(showButton.value) {
        Tracking.track('Click AR Button');
        startLoadingViewer();
        setTimeout(() => {
          SurveyUtils.install();
        }, 2000);
      } else {
        e.preventDefault();
      }
    }

    function closeQRModal(val) {
      showModal.value = val;
    }

    return {
      modelUrl,
      arLaunchUrl,
      model,
      loadingModel,
      loadingViewer,
      openViewer,
      showModal,
      closeQRModal,
      arButton,
      supportsAR,
      iOSChrome,
      showButton,
      debugButton,
      icon,
    };
  }

}
</script>

<style lang="postcss" scoped>
.product-viewer-button {
  width: 100%;
  text-align: center;
  text-transform: initial;
  letter-spacing: normal;
  font-family: var(--font-regular);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-16);
}

.ar-icon {
  width: 16px;
  margin-right: var(--space-2);
}

.ar-button-loader {
  margin-left: var(--space-2);
}
</style>